import React from "react";
import { Link } from "react-router-dom";
import {
  Snowflake,
  Droplets,
  Wrench,
  CircleDot,
  Disc3,
  Cog,
  Stethoscope,
  Zap,
  Lightbulb,
  Battery,
  Sparkles,
  Truck,
  ArrowRight,
} from "lucide-react";

export const SERVICES = [
  {
    id: "ac-repair",
    title: "Klíma javítás-töltés",
    icon: Snowflake,
    short: "Klímarendszer",
    description:
      "Professzionális klímatöltés, szivárgásvizsgálat és teljes körű javítás, hogy a nyári melegben is megőrizze az autója komfortját.",
    bullets: ["R134a / R1234yf gáz", "Szivárgásvizsgálat", "Klímatisztítás"],
    href: "/klima",
    cta: "Részletek és árak",
    accent: "#0082D2",
  },
  {
    id: "oil-change",
    title: "Automataváltó olajcsere",
    icon: Droplets,
    short: "MotulEVO géppel",
    description:
      "Dinamikus olajcsere csúcstechnológiás MotulEVO berendezéssel. Megnövelt váltóélettartam, simább sebességváltás és gyári teljesítmény.",
    bullets: ["ZF, Aisin, DSG", "Eredeti Motul olaj", "Adaptáció reset"],
    href: "/automatavalto-olajcsere",
    cta: "Részletek és árak",
    accent: "#E1132C",
  },
  {
    id: "walnut-blasting",
    title: "Dióhéjas tisztítás",
    icon: Sparkles,
    short: "Walnut blasting",
    description:
      "Direkt befecskendezésű motorok (BMW N54/N55/B58, TFSI/TSI) szívószelep koksz-mentesítése mechanikus dióhéj-fúvatással.",
    bullets: ["Vegyszermentes", "Endoszkópos dokumentáció", "ISTA reset"],
    href: "/diohejas-tisztitas",
    cta: "Részletek",
    accent: "#F59E0B",
  },
  {
    id: "kisteher",
    title: "Kisteher / Ducato",
    icon: Truck,
    short: "Flotta-szervíz",
    description:
      "Fiat Ducato, Renault Master, Mercedes Sprinter, VW Crafter szakszerviz. Flotta-karbantartás minimális állásidővel.",
    bullets: ["X230/X244/X250/X290", "OEM alkatrész", "Műszaki vizsga"],
    href: "/kisteher-szerviz",
    cta: "Részletek",
    accent: "#FF6B1A",
  },
  {
    id: "bmw-service",
    title: "BMW & MINI Szervíz",
    icon: Wrench,
    short: "BMW Group szakszerviz",
    description:
      "Hivatalos online szervíz-vezetés (kulcsba is bejegyezve) — BMW és MINI egyaránt. ISTA / Rheingold diagnosztika, E-Sys kódolás. E-, F-, G-szériák, valamint R50–F60 MINI generációk.",
    bullets: ["BMW + MINI online szervíz-vezetés", "ISTA + E-Sys kódolás", "Vezérműlánc N47/N57/N14/N18"],
    href: "/bmw",
    cta: "Részletek",
    accent: "#0082D2",
  },
  {
    id: "tire-service",
    title: "Gumiszerelés, centrírozás",
    icon: CircleDot,
    short: "Kerékszerviz",
    description:
      "Precíz gumiszerelés és kerékkiegyensúlyozás minden méretben. Nyári-téli gumicsere gyors, kulturált kiszolgálással.",
    bullets: ["13″ – 22″ felnik", "Centrírozás", "Szezonális gumicsere"],
    href: "/#foglalas",
    cta: "Időpontfoglalás",
    accent: "#0082D2",
  },
];

// Expanded sub-service list for the Q-Service section
export const Q_SERVICE_ITEMS = [
  { icon: Droplets, label: "Olajcsere és szűrőcsere" },
  { icon: Disc3, label: "Fékjavítás, féktárcsa, fékbetét" },
  { icon: Cog, label: "Futómű javítás és beállítás" },
  { icon: Stethoscope, label: "Számítógépes diagnosztika" },
  { icon: Zap, label: "Kipufogórendszer javítás" },
  { icon: Battery, label: "Akkumulátor csere és teszt" },
  { icon: Lightbulb, label: "Izzócsere, elektromos hibák" },
  { icon: Wrench, label: "Szíjcsere, vezérműszíj" },
];

export default function Services() {
  return (
    <section
      id="szolgaltatasok"
      className="relative py-24 sm:py-32"
      data-testid="services-section"
    >
      <div className="max-w-7xl mx-auto px-6 lg:px-10">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6 mb-16">
          <div>
            <span className="section-eyebrow mb-4">01 / Szolgáltatások</span>
            <h2
              className="mt-5 font-black uppercase tracking-tight text-white leading-[0.95]"
              style={{
                fontFamily: "Barlow Condensed",
                fontSize: "clamp(2.25rem, 5vw, 4rem)",
              }}
            >
              Profi szolgáltatások
              <br />
              <span className="text-zinc-500">debreceni autósoknak.</span>
            </h2>
          </div>
          <p className="max-w-md text-zinc-400 leading-relaxed">
            A klímatöltéstől a BMW márkadiagnosztikáig — minden, amire egy
            megbízható szervíznek készen kell állnia, egy helyen.{" "}
            <span className="text-white">Kattintson a kártyára a részletekért!</span>
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">
          {SERVICES.map((s, idx) => {
            const Icon = s.icon;
            const isAnchor = s.href.startsWith("/#");
            const Wrapper = isAnchor ? "a" : Link;
            const wrapperProps = isAnchor ? { href: s.href } : { to: s.href };

            return (
              <Wrapper
                key={s.id}
                {...wrapperProps}
                className="service-card fade-up flex flex-col group cursor-pointer hover:border-[#0082D2]/60 transition-colors"
                style={{ animationDelay: `${idx * 0.06}s` }}
                data-testid={`service-card-${s.id}`}
              >
                <div className="flex items-start justify-between mb-8">
                  <div
                    className="w-12 h-12 flex items-center justify-center border transition-colors"
                    style={{
                      backgroundColor: `${s.accent}10`,
                      borderColor: `${s.accent}30`,
                    }}
                  >
                    <Icon
                      className="w-6 h-6"
                      style={{ color: s.accent }}
                      strokeWidth={1.5}
                    />
                  </div>
                  <span
                    className="text-[0.65rem] font-bold uppercase tracking-[0.2em] text-zinc-500"
                    style={{ fontFamily: "Barlow Condensed" }}
                  >
                    0{idx + 1}
                  </span>
                </div>

                <div
                  className="text-[0.7rem] uppercase tracking-[0.2em] text-zinc-500 mb-2"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  {s.short}
                </div>

                <h3
                  className="text-2xl font-bold uppercase text-white leading-tight mb-4"
                  style={{ fontFamily: "Barlow Condensed" }}
                >
                  {s.title}
                </h3>

                <p className="text-sm text-zinc-400 leading-relaxed mb-6 flex-1">
                  {s.description}
                </p>

                <ul className="space-y-2 pt-6 border-t border-white/5">
                  {s.bullets.map((b) => (
                    <li
                      key={b}
                      className="flex items-center gap-2 text-xs text-zinc-300"
                    >
                      <span
                        className="w-1 h-1"
                        style={{ backgroundColor: s.accent }}
                      />
                      {b}
                    </li>
                  ))}
                </ul>

                {/* CTA strip */}
                <div
                  className="mt-6 -mx-6 -mb-6 px-6 py-3 border-t border-white/5 flex items-center justify-between transition-colors group-hover:bg-white/[0.03]"
                >
                  <span
                    className="text-[0.7rem] font-bold uppercase tracking-[0.2em]"
                    style={{
                      fontFamily: "Barlow Condensed",
                      color: s.accent,
                    }}
                  >
                    {s.cta}
                  </span>
                  <ArrowRight
                    className="w-4 h-4 transition-transform group-hover:translate-x-1"
                    style={{ color: s.accent }}
                    strokeWidth={2}
                  />
                </div>
              </Wrapper>
            );
          })}
        </div>

        {/* Q-Service detailed list */}
        <div
          className="mt-20 grid lg:grid-cols-12 gap-10 items-start"
          data-testid="qservice-detail"
        >
          <div className="lg:col-span-4">
            <div className="inline-flex items-center gap-3 mb-5">
              <span
                className="bg-[#0082D2] text-white px-3 py-1 text-xs font-black uppercase tracking-[0.2em]"
                style={{ fontFamily: "Barlow Condensed" }}
              >
                Q-Service
              </span>
              <div className="m-stripe h-[2px] w-16" />
            </div>
            <h3
              className="font-black uppercase text-white leading-[0.95]"
              style={{
                fontFamily: "Barlow Condensed",
                fontSize: "clamp(1.75rem, 3.5vw, 2.75rem)",
              }}
            >
              Hálózati tagként
              <br />
              minden szervízmunkát
              <br />
              vállalunk.
            </h3>
            <p className="mt-5 text-zinc-400 text-sm leading-relaxed">
              A Q-Service hálózat tagjaként garantált minőségű alkatrészekkel
              és gyári szervízelőírások szerint dolgozunk. Egy szervíz,
              minden javítás.
            </p>
          </div>

          <div className="lg:col-span-8 grid sm:grid-cols-2 gap-px bg-white/5 border border-white/5">
            {Q_SERVICE_ITEMS.map((it) => {
              const I = it.icon;
              return (
                <div
                  key={it.label}
                  className="bg-[#0e0e0e] hover:bg-[#141414] transition-colors p-5 flex items-center gap-4"
                >
                  <div className="w-10 h-10 flex items-center justify-center bg-[#0082D2]/10 border border-[#0082D2]/25 shrink-0">
                    <I className="w-5 h-5 text-[#0082D2]" strokeWidth={1.6} />
                  </div>
                  <span className="text-sm text-zinc-200">{it.label}</span>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </section>
  );
}
